import { isNil } from 'lodash'

import { createApplyAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/createApplyAnnouncement'
import { queryAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/queryAnnouncement'
import { updateApplyAnnouncementApi } from '@eastcoal/api/trade/announcementOperation/updateApplyAnnouncement'
import { AnnouncementEntity } from '@eastcoal/api/trade/interface'

import { PageContainer } from '@eastcoal/common/components/Layout/PageContainer'

import { CrudForm } from '@eastcoal/formily/components/CrudForm'
import { SubmitType } from '@eastcoal/formily/components/CrudForm/enums/SubmitType'
import { BackButton } from '@eastcoal/formily/components/CrudForm/shared/BackButton'
import { RefreshButton } from '@eastcoal/formily/components/CrudForm/shared/RefreshButton'
import { SaveButton } from '@eastcoal/formily/components/CrudForm/shared/SaveButton'
import { SubmitButton } from '@eastcoal/formily/components/CrudForm/shared/SubmitButton'
import { FormItem } from '@eastcoal/formily/components/FormItem'
import { Input } from '@eastcoal/formily/components/Input'
import { RichEditor } from '@eastcoal/formily/components/RichEditor'
import { TextArea } from '@eastcoal/formily/components/TextArea'

import { useNavigate } from '@eastcoal/router/exports/react-router'
import { KindAllNodeCascader } from '@eastcoal/shared/@formily/KindAllNodeCascader'
import { MemberSelect } from '@eastcoal/shared/@formily/MemberSelect'
import { MemberType } from '@eastcoal/shared/enums/MemberType'

import { ChannelCascader } from '../../components/ChannelCascader'
import { AnnouncementPublishTimeUpdateStrategy } from '../../enums/AnnouncementPublishTimeUpdateStrategy'
import { AnnouncementStatus } from '../../enums/AnnouncementStatus'
import { ChannelType } from '../../enums/ChannelType'
import { APPLY_ANNOUNCEMENT_LIST_PAGE_PATH } from '../constants/path'

import { PublishTimeUpdateStrategyRadioGroup } from './components/PublishTimeUpdateStrategyRadioGroup'
import { SubmitScheduleButton } from './components/SubmitScheduleButton'
import { productNameEffect } from './effects/productName'
import {
  ApplyAnnouncementFormProps,
  ApplyAnnouncementFormExposedProps,
  ApplyAnnouncementFormData,
  ApplyAnnouncementDetail
} from './interface'

const schema: ApplyAnnouncementFormProps['schema'] = [
  {
    name: 'title',
    title: '文章标题',
    type: 'string',
    required: true,
    'x-decorator': FormItem,
    'x-component': Input,
    'x-component-props': {
      maxLength: 50,
      showCount: true,
      placeholder: '请输入标题，限50个字符以内'
    }
  },
  {
    name: 'publishTimeUpdateStrategy',
    title: '发布时间更新策略',
    type: 'number',
    required: true,
    default: AnnouncementPublishTimeUpdateStrategy.FIRST_PUBLISHED,
    'x-decorator': FormItem,
    'x-component': PublishTimeUpdateStrategyRadioGroup
  },
  {
    name: 'timingPublishTime',
    title: '定时发布时间',
    type: 'string'
  },
  {
    name: '{id:channelId, name:channelName}',
    title: '所属频道',
    type: 'object',
    required: true,
    'x-decorator': FormItem,
    'x-component': ChannelCascader,
    'x-component-props': {
      type: ChannelType.APPLY_ANNOUNCEMENT
    }
  },
  {
    name: '{id: memberId, name: memberName}',
    title: '委托方',
    type: 'object',
    required: true,
    'x-component': MemberSelect,
    'x-component-props': {
      type: MemberType.ENTERPRISE
    },
    'x-decorator': FormItem
  },
  {
    name: 'kindList',
    title: '品种',
    type: 'array',
    required: true,
    'x-component': KindAllNodeCascader,
    'x-component-props': {
      multiple: true
    },
    'x-decorator': FormItem
  },
  {
    name: 'productName',
    title: '标的物',
    type: 'string',
    required: true,
    'x-decorator': FormItem,
    'x-pattern':
      '{{!$form.editable ? "readPretty" :$values.kindList.length ?"editable":"disabled" }}',
    'x-component': Input,
    'x-component-props': {
      placeholder: '请先选择品种'
    }
  },
  {
    name: 'description',
    title: '文章摘要',
    type: 'string',
    'x-decorator': FormItem,
    'x-decorator-props': {
      gridSpan: 2
    },
    'x-component': TextArea,
    'x-component-props': {
      maxLength: 300,
      showCount: true,
      placeholder: '请输入摘要内容，限300个字符以内'
    }
  },
  {
    name: 'content',
    title: '文章正文',
    type: 'string',
    'x-decorator': FormItem,
    'x-decorator-props': {
      gridSpan: 2
    },
    'x-component': RichEditor
  }
]

const detailFormatter = ({
  kindIds,
  kindNames,
  ...rest
}: AnnouncementEntity): ApplyAnnouncementDetail => {
  const kindIdArr = kindIds?.split(',') ?? []
  const kindNameArr = kindNames?.split(',') ?? []
  return {
    ...rest,
    kindList: kindIdArr.map((kind, index) => ({
      id: kind,
      name: kindNameArr[index]
    }))
  }
}
const readApi: ApplyAnnouncementFormProps['readApi'] = ({ id }) => {
  return {
    api: () => queryAnnouncementApi({ id }).then(data => detailFormatter(data)),
    ready: !isNil(id)
  }
}

const formattedFormData = (
  { kindList = [], ...rest }: ApplyAnnouncementFormData,
  submitType?: SubmitType
) => {
  return {
    ...rest,
    kindIds: kindList.map(kind => kind.id).join(),
    kindNames: kindList.map(kind => kind.name).join(),
    status:
      submitType === SubmitType.SAVE
        ? AnnouncementStatus.SAVED
        : rest.timingPublishTime
          ? AnnouncementStatus.WAIT_PUBLISH
          : AnnouncementStatus.PUBLISHED
  }
}

const createApi: ApplyAnnouncementFormProps['createApi'] = (
  values,
  { submitType }
) => {
  return createApplyAnnouncementApi(formattedFormData(values, submitType))
}

const updateApi: ApplyAnnouncementFormProps['updateApi'] = (
  values,
  { submitType }
) => {
  return updateApplyAnnouncementApi(formattedFormData(values, submitType))
}

export const ApplyAnnouncementForm = ({
  mode,
  initialValues
}: ApplyAnnouncementFormExposedProps) => {
  const navigator = useNavigate()
  return (
    <PageContainer>
      <CrudForm
        createApi={createApi}
        initialValues={initialValues}
        mode={mode}
        name='applyAnnouncementForm'
        readApi={readApi}
        schema={schema}
        updateApi={updateApi}
        actions={[
          <BackButton key='back' />,
          <SaveButton key='saveButton' />,
          <SubmitButton key='submitButton'>直接发布</SubmitButton>,
          <SubmitScheduleButton key='submitScheduleButton' />,
          <RefreshButton key='refreshButton' />
        ]}
        effects={() => {
          productNameEffect()
        }}
        grid={{
          columns: 2
        }}
        onSuccess={() => navigator(APPLY_ANNOUNCEMENT_LIST_PAGE_PATH)}
      />
    </PageContainer>
  )
}
